import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  ChevronDown,
  ChevronUp,
  Save,
  BookOpen,
  RefreshCw,
  Calendar,
  DollarSign,
  ShieldCheck,
  ShieldOff,
  Info,
  AlertCircle,
  HelpCircle,
} from "lucide-react";
import { patronTypesApi, type CreatePatronTypeDto } from "@/lib/api/patron-types";
import { PATRON_TYPES_QUERY_KEY } from "@/hooks/usePatronTypes";
import {
  PATRON_TYPE_PERMISSION_GROUPS,
  getAllPatronPermissionKeys,
} from "@/lib/patron-type-permissions";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { cn } from "@/lib/utils";

const PatronTypeFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Form state
  const [formData, setFormData] = useState<
    Omit<CreatePatronTypeDto, "borrowLimit" | "renewalLimit" | "borrowingPeriodDays"> & {
      borrowLimit?: number;
      renewalLimit?: number;
      borrowingPeriodDays?: number;
    }
  >({
    name: "",
    description: "",
    permissions: [],
    borrowLimit: undefined,
    renewalLimit: undefined,
    borrowingPeriodDays: undefined,
    fineRatePerDay: undefined,
    isActive: true,
  });
  const isEditing = !!id;

  useEffect(() => {
    if (isTenantAdmin && isEditing && id) {
      loadPatronType(id);
    }
  }, [isTenantAdmin, isEditing, id]);

  // Track unsaved changes
  useEffect(() => {
    if (!loading) {
      setHasUnsavedChanges(true);
    }
  }, [formData]);

  const loadPatronType = async (patronTypeId: string) => {
    try {
      setLoading(true);
      const patronType = await patronTypesApi.getById(patronTypeId);
      setFormData({
        name: patronType.name,
        description: patronType.description || "",
        permissions: [...patronType.permissions],
        borrowLimit: patronType.borrowLimit,
        renewalLimit: patronType.renewalLimit,
        borrowingPeriodDays: patronType.borrowingPeriodDays,
        fineRatePerDay: patronType.fineRatePerDay || undefined,
        isActive: patronType.isActive,
      });
      // Expand all groups when editing
      setExpandedGroups(new Set(PATRON_TYPE_PERMISSION_GROUPS.map((g) => g.label)));
      setHasUnsavedChanges(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load patron type.");
      navigate("/setup/patron-types");
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "name":
        if (!value || !value.trim()) return "Name is required";
        if (value.length < 2) return "Name must be at least 2 characters";
        if (value.length > 50) return "Name must be less than 50 characters";
        return "";
      case "borrowLimit":
        if (value === undefined || value === "") return "Borrow limit is required";
        if (value < 0) return "Cannot be negative";
        if (value > 100) return "Cannot exceed 100";
        return "";
      case "renewalLimit":
        if (value === undefined || value === "") return "Renewal limit is required";
        if (value < 0) return "Cannot be negative";
        if (value > 10) return "Cannot exceed 10";
        return "";
      case "borrowingPeriodDays":
        if (value === undefined || value === "") return "Loan period is required";
        if (value < 0) return "Cannot be negative";
        if (value > 365) return "Cannot exceed 365 days";
        return "";
      default:
        return "";
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    newErrors.name = validateField("name", formData.name);
    newErrors.borrowLimit = validateField("borrowLimit", formData.borrowLimit);
    newErrors.renewalLimit = validateField("renewalLimit", formData.renewalLimit);
    newErrors.borrowingPeriodDays = validateField(
      "borrowingPeriodDays",
      formData.borrowingPeriodDays
    );

    // Filter out empty errors
    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, v]) => v !== "")
    );

    setErrors(filteredErrors);
    // Mark all fields as touched
    setTouched(new Set(["name", "borrowLimit", "renewalLimit", "borrowingPeriodDays"]));

    return Object.keys(filteredErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
    const value = formData[field as keyof typeof formData];
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    try {
      setSaving(true);
      const submitData: CreatePatronTypeDto = {
        ...formData,
        borrowLimit: formData.borrowLimit!,
        renewalLimit: formData.renewalLimit!,
        borrowingPeriodDays: formData.borrowingPeriodDays!,
      };

      if (isEditing && id) {
        await patronTypesApi.update(id, submitData);
        toast.success(`"${formData.name}" has been updated successfully.`);
      } else {
        await patronTypesApi.create(submitData);
        toast.success(`"${formData.name}" has been created successfully.`);
      }

      queryClient.invalidateQueries(PATRON_TYPES_QUERY_KEY);
      setHasUnsavedChanges(false);
      navigate("/setup/patron-types");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation("/setup/patron-types");
      setShowDiscardDialog(true);
    } else {
      navigate("/setup/patron-types");
    }
  };

  const handleDiscardConfirm = () => {
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const togglePermission = (permissionKey: string) => {
    setFormData((prev) => {
      const permissions = prev.permissions.includes(permissionKey)
        ? prev.permissions.filter((p) => p !== permissionKey)
        : [...prev.permissions, permissionKey];
      return { ...prev, permissions };
    });
  };

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupLabel)) {
        newSet.delete(groupLabel);
      } else {
        newSet.add(groupLabel);
      }
      return newSet;
    });
  };

  const toggleAllPermissions = (checked: boolean) => {
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        permissions: getAllPatronPermissionKeys(),
      }));
      setExpandedGroups(new Set(PATRON_TYPE_PERMISSION_GROUPS.map((g) => g.label)));
    } else {
      setFormData((prev) => ({ ...prev, permissions: [] }));
    }
  };

  const getFieldStatus = (field: string) => {
    if (!touched.has(field)) return "default";
    if (errors[field]) return "error";
    return "success";
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage patron types.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading && isEditing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading patron type..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer>
        <div className="mb-6">
          <PageHeader
            title={isEditing ? "Edit Patron Type" : "Create Patron Type"}
            description={
              isEditing
                ? "Update the settings and permissions for this patron type"
                : "Set up a new category for library members with specific borrowing rules"
            }
            breadcrumbItems={[
              { label: "Setup", href: "/setup" },
              { label: "Patron Types", href: "/setup/patron-types" },
              { label: isEditing ? "Edit" : "New" },
            ]}
            showSearch={false}
          />
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
              <div className="p-2 rounded-lg bg-primary/10">
                <Info className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                <p className="text-sm text-gray-500">
                  Give this patron type a name and description
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="flex items-center gap-1 mb-2">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onBlur={() => handleFieldBlur("name")}
                  placeholder="e.g., Student, Faculty, Community Member"
                  className={cn(
                    getFieldStatus("name") === "error" && "border-red-500 focus:border-red-500",
                    getFieldStatus("name") === "success" &&
                      "border-green-500 focus:border-green-500"
                  )}
                />
                {touched.has("name") && errors.name && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="description" className="flex items-center gap-1 mb-2">
                  Description
                  <span className="text-xs font-normal text-gray-400">(optional)</span>
                </Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Briefly describe who belongs to this patron type"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  This helps staff understand who should be assigned to this type.
                </p>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <Checkbox
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked === true })
                  }
                />
                <div className="flex-1">
                  <Label htmlFor="isActive" className="font-medium cursor-pointer">
                    Active
                  </Label>
                  <p className="text-xs text-gray-500">
                    Only active patron types can be assigned to new members
                  </p>
                </div>
                <span
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium",
                    formData.isActive ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                  )}
                >
                  {formData.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </Card>

          {/* Borrowing Rules */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
              <div className="p-2 bg-teal-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Borrowing Rules</h3>
                <p className="text-sm text-gray-500">Set limits for how members can borrow items</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="borrowLimit" className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                  Maximum books at once <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="borrowLimit"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.borrowLimit ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      borrowLimit: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  onBlur={() => handleFieldBlur("borrowLimit")}
                  placeholder="e.g., 5"
                  className={cn(
                    getFieldStatus("borrowLimit") === "error" &&
                      "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("borrowLimit") && errors.borrowLimit && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.borrowLimit}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-500">
                  How many items can be checked out at the same time (0 = no checkouts)
                </p>
              </div>

              <div>
                <Label htmlFor="renewalLimit" className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-gray-400" />
                  Renewal limit <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="renewalLimit"
                  type="number"
                  min="0"
                  max="10"
                  value={formData.renewalLimit ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      renewalLimit: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  onBlur={() => handleFieldBlur("renewalLimit")}
                  placeholder="e.g., 2"
                  className={cn(
                    getFieldStatus("renewalLimit") === "error" &&
                      "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("renewalLimit") && errors.renewalLimit && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.renewalLimit}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-500">
                  How many times a loan can be extended (0 = no renewals)
                </p>
              </div>

              <div>
                <Label htmlFor="borrowingPeriodDays" className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  Loan period (days) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="borrowingPeriodDays"
                  type="number"
                  min="0"
                  max="365"
                  value={formData.borrowingPeriodDays ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      borrowingPeriodDays: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  onBlur={() => handleFieldBlur("borrowingPeriodDays")}
                  placeholder="e.g., 14"
                  className={cn(
                    getFieldStatus("borrowingPeriodDays") === "error" &&
                      "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("borrowingPeriodDays") && errors.borrowingPeriodDays && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.borrowingPeriodDays}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-500">
                  How long members can keep borrowed items (0 = due same day, end of day)
                </p>
              </div>

              <div>
                <Label htmlFor="fineRatePerDay" className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  Late fee per day
                  <span className="text-xs font-normal text-gray-400">(optional)</span>
                </Label>
                <Input
                  id="fineRatePerDay"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.fineRatePerDay ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fineRatePerDay: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  placeholder="0.00"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Amount charged per day for overdue items (leave empty for no fines)
                </p>
              </div>
            </div>
          </Card>

          {/* Permissions */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 pb-4 mb-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <ShieldCheck className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Permissions</h3>
                  <p className="text-sm text-gray-500">Choose what members of this type can do</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={formData.permissions.length === getAllPatronPermissionKeys().length}
                  onCheckedChange={(checked) => toggleAllPermissions(checked === true)}
                />
                <Label htmlFor="select-all" className="text-sm text-gray-600 cursor-pointer">
                  Select all
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              {PATRON_TYPE_PERMISSION_GROUPS.map((group) => {
                const isExpanded = expandedGroups.has(group.label);
                const groupPermissions = group.permissions.map((p) => p.key);
                const selectedCount = groupPermissions.filter((key) =>
                  formData.permissions.includes(key)
                ).length;
                const allSelected = selectedCount === groupPermissions.length;
                const someSelected = selectedCount > 0 && selectedCount < groupPermissions.length;

                return (
                  <div
                    key={group.label}
                    className={cn(
                      "border rounded-lg transition-colors",
                      isExpanded ? "border-gray-200 bg-gray-50/50" : "border-gray-100"
                    )}
                  >
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleGroup(group.label)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={(checked) => {
                            if (checked === true) {
                              setFormData((prev) => ({
                                ...prev,
                                permissions: [
                                  ...new Set([...prev.permissions, ...groupPermissions]),
                                ],
                              }));
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                permissions: prev.permissions.filter(
                                  (p) => !groupPermissions.includes(p)
                                ),
                              }));
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className={cn(someSelected && "data-[state=unchecked]:bg-primary/30")}
                        />
                        <div>
                          <span className="font-medium text-gray-900">{group.label}</span>
                          <span className="ml-2 text-xs text-gray-500">
                            {selectedCount} of {groupPermissions.length} selected
                          </span>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {isExpanded && (
                      <div className="px-4 pt-0 pb-4 space-y-3 ml-7">
                        {group.permissions.map((permission) => (
                          <div
                            key={permission.key}
                            className="flex items-start gap-3 p-2 rounded hover:bg-white"
                          >
                            <Checkbox
                              id={permission.key}
                              checked={formData.permissions.includes(permission.key)}
                              onCheckedChange={() => togglePermission(permission.key)}
                            />
                            <div className="flex-1">
                              <Label
                                htmlFor={permission.key}
                                className="font-normal text-gray-700 cursor-pointer"
                              >
                                {permission.label}
                              </Label>
                              {permission.description && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {permission.description}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {formData.permissions.length === 0 && (
              <div className="flex items-start gap-3 p-3 mt-4 bg-gray-50 border border-gray-200 rounded-lg">
                <HelpCircle className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-700">No permissions selected</p>
                  <p className="text-xs text-gray-500">
                    Members with this patron type won't be able to perform any actions until you
                    grant permissions.
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? "Save Changes" : "Create Patron Type"}
                </>
              )}
            </Button>
          </div>
        </form>
      </PageContainer>

      {/* Discard Changes Dialog */}
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        title="Discard unsaved changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={handleDiscardConfirm}
        variant="warning"
      />
    </AppLayout>
  );
};

export default PatronTypeFormPage;
